import { GET, POST, PUT, DELETE } from '../../../services/api'
import {
  SellerQueueItem,
  SellerServiceSessionItem,
  SellerServiceNoSaleReasonItem,
  NoSaleReasonCodeOption,
  NoSaleReasonStat,
  SellerConfigItem,
  ConferenceOrderFilterPayload,
  ConferenceOrderItem,
  ConferenceSeller,
} from './types'

const normalizeApiData = <T>(response: any): T => {
  if (response && typeof response === 'object' && 'data' in response) {
    return response.data as T
  }

  return response as T
}

// ============ SELLER QUEUE API ============

export const getQueueByPluginspace = async (): Promise<SellerQueueItem[]> => {
  try {
    console.log('[services] getQueueByPluginspace - Fetching from /seller-queue/pluginspace')
    const response = await GET(`/seller-queue/pluginspace`)
    const data = normalizeApiData<SellerQueueItem[]>(response) || []
    console.log('[services] getQueueByPluginspace - Got response with', data.length, 'items')
    if (data.length > 0) {
      console.log('[services] getQueueByPluginspace - First item full structure:', JSON.stringify(data[0], null, 2))
    }
    return data
  } catch (error) {
    console.error('[services] getQueueByPluginspace - ERROR:', error)
    throw error
  }
}

export const getQueueByStatus = async (
  status: string
): Promise<SellerQueueItem[]> => {
  try {
    const response = await GET(`/seller-queue/status/${status}`)
    return normalizeApiData<SellerQueueItem[]>(response) || []
  } catch (error) {
    throw error
  }
}

export const getNextAvailable = async (): Promise<SellerQueueItem | null> => {
  try {
    const response = await GET(`/seller-queue/next-available`)
    return normalizeApiData<SellerQueueItem | null>(response) || null
  } catch (error) {
    throw error
  }
}

export const createQueueEntry = async (
  pluginspaceId: number,
  sellerId: number,
  position?: number
): Promise<SellerQueueItem> => {
  try {
    const response = await POST('/seller-queue/createQueueEntry', {
      pluginspaceId,
      sellerId,
      ...(position && position > 0 ? { position } : {}),
    })
    return normalizeApiData<SellerQueueItem>(response)
  } catch (error) {
    throw error
  }
}

export const updateQueueStatus = async (id: number, status: string): Promise<void> => {
  try {
    await PUT(`/seller-queue/${id}/status`, { status })
  } catch (error) {
    throw error
  }
}

export const updateQueuePosition = async (
  sellerId: number,
  newPosition: number,
  pluginspaceId: number
): Promise<void> => {
  try {
    await PUT('/seller-queue/updatePosition', { sellerId, newPosition, pluginspaceId })
  } catch (error) {
    throw error
  }
}

export const batchUpdateQueuePositions = async (
  updates: Array<{ sellerId: number; position: number }>,
  authCredential?: string
): Promise<void> => {
  try {
    const payload = { updates, ...(authCredential ? { authCredential } : {}) }
    console.log('[services] batchUpdateQueuePositions - START - Sending', updates.length, 'updates')
    // Axios aqui tem timeout de 15s, então não precisa de withTimeout adicional
    const response = await PUT('/seller-queue/batchUpdatePositions', payload)
    console.log('[services] batchUpdateQueuePositions - SUCCESS - Response:', response)
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error)
    console.error('[services] batchUpdateQueuePositions - ERROR:', errorMsg)
    throw new Error(`Falha ao atualizar fila: ${errorMsg}`)
  }
}

export const updateLastCalledAt = async (id: number): Promise<void> => {
  try {
    await PUT(`/seller-queue/${id}/last-called`, {})
  } catch (error) {
    throw error
  }
}

export const updateLastFinishedAt = async (id: number): Promise<void> => {
  try {
    await PUT(`/seller-queue/${id}/last-finished`, {})
  } catch (error) {
    throw error
  }
}

export const removeFromQueue = async (
  sellerId: number
): Promise<void> => {
  try {
    console.log(`[removeFromQueue] Starting removal of sellerId=${sellerId}`);
    const response = await DELETE(`/seller-queue/seller/${sellerId}`);
    console.log(`[removeFromQueue] Response:`, response);
    
    if (response.success === false) {
      throw new Error(response.error || response.message || 'Falha ao remover');
    }
    
    console.log(`[removeFromQueue] ✅ Successfully removed sellerId=${sellerId}`);
  } catch (error) {
    console.error(`[removeFromQueue] ❌ Error removing sellerId=${sellerId}:`, error);
    throw error;
  }
}

export const startSellerAttendance = async (
  sellerId: number,
  queuePositionAtStart?: number
): Promise<void> => {
  try {
    const normalizedQueuePosition = Number(queuePositionAtStart)
    const shouldSendQueuePosition =
      Number.isInteger(normalizedQueuePosition) && normalizedQueuePosition > 0

    const response = await POST('/seller-queue/start-attendance', {
      sellerId,
      ...(shouldSendQueuePosition ? { queuePositionAtStart: normalizedQueuePosition } : {}),
    })
    if (response?.success === false) {
      throw new Error(response?.message || 'Falha ao iniciar atendimento')
    }
  } catch (error) {
    throw error
  }
}

export const closeSellerAttendance = async (
  sessionId: number,
  sellerId: number,
  outcome: 'sale_completed' | 'pending_checkout' | 'no_sale' | 'abandoned',
  reasonCode?: number,
  note?: string
): Promise<void> => {
  try {
    const response = await POST('/seller-queue/close-attendance', {
      sessionId,
      sellerId,
      outcome,
      reasonCode,
      note,
    })
    if (response?.success === false) {
      throw new Error(response?.message || 'Falha ao encerrar atendimento')
    }
  } catch (error) {
    throw error
  }
}

// ============ SELLER SERVICE SESSION API ============

export const getSessionHistory = async (
  pluginspaceId: number,
  startDate?: string,
  endDate?: string
): Promise<SellerServiceSessionItem[]> => {
  try {
    let url = `/seller-service-session/pluginspace/${pluginspaceId}`
    if (startDate && endDate) {
      url += `?startDate=${startDate}&endDate=${endDate}`
    }
    const response = await GET(url)
    const rawSessions = normalizeApiData<SellerServiceSessionItem[]>(response) || []

    return (rawSessions || []).map((session: any) => ({
      ...session,
      sellerName:
        session?.sellerName ||
        session?.sellerQueue?.user?.name ||
        session?.sellerQueue?.user?.username ||
        undefined,
    }))
  } catch (error) {
    throw error
  }
}

export const getSessionById = async (sessionId: number): Promise<SellerServiceSessionItem> => {
  try {
    const response = await GET(`/seller-service-session/${sessionId}`)
    return normalizeApiData<SellerServiceSessionItem>(response)
  } catch (error) {
    throw error
  }
}

export const updateSessionStatus = async (sessionId: number, status: string): Promise<void> => {
  try {
    await PUT(`/seller-service-session/${sessionId}`, { status })
  } catch (error) {
    throw error
  }
}

export const completeSession = async (
  sessionId: number,
  outcome: string,
  durationSeconds: number
): Promise<void> => {
  try {
    await PUT(`/seller-service-session/${sessionId}`, { outcome, durationSeconds })
  } catch (error) {
    throw error
  }
}

// ============ NO SALE REASONS API ============

export const getNoSaleReasonsBySession = async (
  sessionId: number
): Promise<SellerServiceNoSaleReasonItem[]> => {
  try {
    const response = await GET(`/seller-service-no-sale-reason/session/${sessionId}`)
    return normalizeApiData<SellerServiceNoSaleReasonItem[]>(response) || []
  } catch (error) {
    throw error
  }
}

export const createNoSaleReason = async (
  sessionId: number,
  reasonCode: number,
  productName?: string,
  sizeWanted?: string,
  colorWanted?: string,
  note?: string
): Promise<SellerServiceNoSaleReasonItem> => {
  try {
    const response = await POST('/seller-service-no-sale-reason', {
      sessionId,
      reasonCode,
      productName,
      sizeWanted,
      colorWanted,
      note,
    })
    return normalizeApiData<SellerServiceNoSaleReasonItem>(response)
  } catch (error) {
    throw error
  }
}

export const getNoSaleReasonCodes = async (): Promise<NoSaleReasonCodeOption[]> => {
  try {
    const response = await GET('/seller-service-no-sale-reason/reason-codes')
    return normalizeApiData<NoSaleReasonCodeOption[]>(response) || []
  } catch (error) {
    throw error
  }
}

export const getNoSaleReasonStatistics = async (
  pluginspaceId: number,
  days?: number,
  startDate?: string,
  endDate?: string,
): Promise<NoSaleReasonStat[]> => {
  try {
    let url = `/seller-service-no-sale-reason/statistics/${pluginspaceId}`
    if (days) {
      url += `?days=${days}`
    }

    if (startDate) {
      url += `${url.includes('?') ? '&' : '?'}startDate=${startDate}`
    }

    if (endDate) {
      url += `${url.includes('?') ? '&' : '?'}endDate=${endDate}`
    }

    const response = await GET(url)
    return normalizeApiData<NoSaleReasonStat[]>(response) || []
  } catch (error) {
    throw error
  }
}

export const getSessionStatistics = async (
  pluginspaceId: number,
  sellerId?: number
): Promise<any> => {
  try {
    let url = `/seller-service-session/statistics?pluginspaceId=${pluginspaceId}`
    if (sellerId) {
      url += `&sellerId=${sellerId}`
    }
    const response = await GET(url)
    return normalizeApiData<any>(response)
  } catch (error) {
    throw error
  }
}

// ============ USERS API ============

export const getUsersByType = async (type: string = 'seller'): Promise<any[]> => {
  try {
    const response = await POST(`/users/filter`, { type: 'seller', status: true })
    return normalizeApiData<any[]>(response) || []
  } catch (error) {
    throw error
  }
}

export const getUsersByTypeRoute = async (userType: string = 'seller'): Promise<ConferenceSeller[]> => {
  try {
    const response = await GET(`/users/type/${userType}`)
    const data = normalizeApiData<ConferenceSeller[]>(response) || []

    return data.filter((user) => user.type === userType && user.status !== false)
  } catch (error) {
    throw error
  }
}

export const filterAdminQueueSellerOrders = async (
  payload: ConferenceOrderFilterPayload,
  page: number = 1,
  take: number = 5000,
  order: 'ASC' | 'DESC' = 'DESC'
): Promise<ConferenceOrderItem[]> => {
  try {
    const response = await POST(
      `/orders/filter-admin-queue-seller?page=${page}&take=${take}&order=${order}`,
      payload
    )

    if (Array.isArray(response)) {
      return response as ConferenceOrderItem[]
    }

    if (response && typeof response === 'object') {
      const directData = (response as { data?: unknown }).data
      if (Array.isArray(directData)) {
        return directData as ConferenceOrderItem[]
      }

      if (
        directData &&
        typeof directData === 'object' &&
        Array.isArray((directData as { data?: unknown }).data)
      ) {
        return (directData as { data: ConferenceOrderItem[] }).data
      }
    }

    return []
  } catch (error) {
    throw error
  }
}

export const authorizeSellerQueueAction = async (
  actionType:
    | 'manual_queue_move'
    | 'return_to_queue'
    | 'conference_search_all'
    | 'conference_search_seller',
  credential: string,
  targetSellerId?: number
): Promise<{ authorized: boolean; message?: string }> => {
  try {
    const response = await POST('/seller-queue/authorize-action', {
      actionType,
      credential,
      ...(targetSellerId ? { targetSellerId } : {}),
    })
    return {
      authorized: !!response.authorized,
      message: response.message,
    }
  } catch (error) {
    return {
      authorized: false,
      message: error instanceof Error ? error.message : String(error),
    }
  }
}

export const hasTotvsIntegrationForConference = async (): Promise<boolean> => {
  try {
    const response = await POST('/pluginconfig/filter', {
      plugintypeId: 17,
      plugintemplateId: 23,
    })

    if (Array.isArray(response)) {
      return response.length > 0
    }

    if (response && typeof response === 'object') {
      const data = (response as { data?: unknown }).data

      if (Array.isArray(data)) {
        return data.length > 0
      }

      if (data && typeof data === 'object' && Array.isArray((data as { data?: unknown }).data)) {
        return ((data as { data: unknown[] }).data || []).length > 0
      }
    }

    return false
  } catch (error) {
    return false
  }
}

export const returnSellerToQueue = async (
  sessionId: number,
  sellerId: number,
  credential: string,
  outcome: 'sale_completed' | 'no_sale',
  reasonCode?: number,
  note?: string
): Promise<void> => {
  try {
    await POST('/seller-queue/return-to-queue', {
      sessionId,
      sellerId,
      credential,
      outcome,
      reasonCode,
      note,
    })
  } catch (error) {
    throw error
  }
}

// ============ SELLER CONFIG API ============

export const getSellerConfig = async (): Promise<SellerConfigItem | null> => {
  try {
    console.log('[services] getSellerConfig - Fetching from /seller-config/pluginspace')
    const response = await GET('/seller-config/pluginspace')
    console.log('[services] getSellerConfig - Full response:', response)
    
    // Aceita payload direto, {data: ...} ou {data: {data: ...}}
    const configData = response?.data?.data || response?.data || response
    console.log('[services] getSellerConfig - Extracted config:', configData)
    
    return configData || null
  } catch (error) {
    console.error('[services] getSellerConfig - ERROR:', error)
    throw error
  }
}

export const createSellerConfig = async (
  configData: SellerConfigItem
): Promise<SellerConfigItem> => {
  try {
    console.log('[services] createSellerConfig - POST /seller-config/create', configData)
    const response = await POST('/seller-config/create', configData)
    console.log('[services] createSellerConfig - Full response:', response)
    
    // Aceita payload direto, {data: ...} ou {data: {data: ...}}
    const result = response?.data?.data || response?.data || response
    console.log('[services] createSellerConfig - Extracted result:', result)
    
    return result
  } catch (error) {
    console.error('[services] createSellerConfig - ERROR:', error)
    throw error
  }
}

export const updateSellerConfig = async (
  configData: SellerConfigItem
): Promise<SellerConfigItem> => {
  try {
    console.log('[services] updateSellerConfig - PUT /seller-config/update', configData)
    const response = await PUT('/seller-config/update', configData)
    console.log('[services] updateSellerConfig - Full response:', response)
    
    // Aceita payload direto, {data: ...} ou {data: {data: ...}}
    const result = response?.data?.data || response?.data || response
    console.log('[services] updateSellerConfig - Extracted result:', result)
    
    return result
  } catch (error) {
    console.error('[services] updateSellerConfig - ERROR:', error)
    throw error
  }
}

export const deleteSellerConfig = async (): Promise<void> => {
  try {
    console.log('[services] deleteSellerConfig - DELETE /seller-config/delete')
    await DELETE('/seller-config/delete')
    console.log('[services] deleteSellerConfig - Success')
  } catch (error) {
    console.error('[services] deleteSellerConfig - ERROR:', error)
    throw error
  }
}
